import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { QuotaProvider } from './context/QuotaContext';
import { CartProvider } from './context/CartContext';
import Layout from './components/Layout';
const Home = lazy(() => import('./pages/Home'));

import { useMaintenance } from './hooks/useMaintenance';
import Maintenance from './pages/Maintenance';

function MaintenanceGuard({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const { isActive, loading } = useMaintenance();

    if (loading) return <PageLoader />;

    // If Admin, bypass maintenance
    if (isActive && (!user || !user.is_admin)) {
        return <Maintenance />;
    }

    return <>{children}</>;
}

// Eager Load Critical Auth Pages
const Register = lazy(() => import('./pages/Register'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword').then(module => ({ default: module.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword').then(module => ({ default: module.ResetPassword })));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));

// Lazy Load Admin Zone
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminClubs = lazy(() => import('./pages/admin/Clubs'));
const AdminFeedbacks = lazy(() => import('./pages/admin/Feedbacks'));
const AdminUserList = lazy(() => import('./pages/admin/UserList'));
const AdminMatchList = lazy(() => import('./pages/admin/MatchList'));
const AdminMatchDetails = lazy(() => import('./pages/admin/MatchDetails'));
const AdminValidations = lazy(() => import('./pages/admin/ValidationsPage'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminShowroom = lazy(() => import('./pages/admin/Showroom'));
const AdminShop = lazy(() => import('./pages/admin/AdminShop'));
const AdminPlans = lazy(() => import('./pages/admin/Plans'));
const AdminPushSubscriptions = lazy(() => import('./pages/admin/PushSubscriptions'));
const AdminModeration = lazy(() => import('./pages/admin/ModerationPage'));
const AdminTickets = lazy(() => import('./pages/admin/AdminTickets'));
const AdminMessages = lazy(() => import('./pages/admin/AdminMessages'));
const AdminPolls = lazy(() => import('./pages/admin/AdminPolls'));
const AdminFeed = lazy(() => import('./pages/admin/AdminFeed'));
const AdminTransactions = lazy(() => import('./pages/admin/Transactions'));
const AdminTournaments = lazy(() => import('./pages/admin/Tournaments'));
const AdminMobilePreview = lazy(() => import('./pages/admin/MobilePreview'));
const AdminFederations = lazy(() => import('./pages/admin/FederationsManagement'));
const AdminSocial = lazy(() => import('./pages/admin/AdminSocial'));
const TournamentWizard = lazy(() => import('./pages/admin/TournamentWizard'));
const TournamentDetails = lazy(() => import('./pages/admin/TournamentDetails'));
const TournamentTV = lazy(() => import('./pages/public/TournamentTV'));
const CourtTV = lazy(() => import('./pages/public/CourtTV'));
const ClubTV = lazy(() => import('./pages/public/ClubTV'));
const CheckInPage = lazy(() => import('./pages/public/CheckInPage'));
const TVSetup = lazy(() => import('./pages/public/TVSetup'));
const ProPlayerProfile = lazy(() => import('./pages/pro/ProPlayerProfile'));
const ProTournamentCalendar = lazy(() => import('./pages/pro/ProTournamentCalendar'));
const ProRankings = lazy(() => import('./pages/pro/ProRankings'));
const SubscriptionManager = lazy(() => import('./pages/pro/SubscriptionManager'));


// Lazy Load Pro Zone
const ProLayout = lazy(() => import('./components/ProLayout'));
const ProDashboard = lazy(() => import('./pages/pro/Dashboard'));
const ProPlanning = lazy(() => import('./pages/pro/Planning'));
const ProSettings = lazy(() => import('./pages/pro/Settings'));
const ProCourts = lazy(() => import('./pages/pro/CourtManager'));
const LayoutEditor = lazy(() => import('./pages/pro/LayoutEditor'));
const Supervision = lazy(() => import('./pages/pro/Supervision'));
const DeviceManager = lazy(() => import('./pages/pro/DeviceManager'));
const Automations = lazy(() => import('./pages/pro/Automations'));
const AppProDashboard = lazy(() => import('./pages/pro/AppDashboard'));
const AppProPlanning = lazy(() => import('./pages/pro/AppPlanning'));
const ProTickets = lazy(() => import('./pages/pro/ProTickets'));
const ProTransactions = lazy(() => import('./pages/pro/Transactions'));
const ProTeam = lazy(() => import('./pages/pro/Team').then(module => ({ default: module.Team })));
const ProPlayers = lazy(() => import('./pages/pro/ProPlayers'));
const ProProfile = lazy(() => import('./pages/pro/UserProfile'));
const ProSponsors = lazy(() => import('./pages/pro/Sponsors'));
const ManagerOnboarding = lazy(() => import('./pages/pro/ManagerOnboarding'));
const ShopRequestForm = lazy(() => import('./pages/pro/marketplace/ShopRequestForm'));
const ShopStatus = lazy(() => import('./pages/pro/marketplace/ShopStatus'));
const ShopSettings = lazy(() => import('./pages/pro/marketplace/ShopSettings'));
const MarketplaceCatalog = lazy(() => import('./pages/pro/marketplace/Catalog'));
const MerchantOrders = lazy(() => import('./pages/pro/marketplace/Orders'));
const MarketplaceInventory = lazy(() => import('./pages/pro/marketplace/Inventory'));
const MarketplaceReviews = lazy(() => import('./pages/pro/marketplace/Reviews'));
const ClubReviews = lazy(() => import('./pages/pro/ClubReviews'));
const MarketplaceSuppliers = lazy(() => import('./pages/pro/marketplace/Suppliers'));
const RentalMonitor = lazy(() => import('./pages/pro/RentalMonitor'));
const AcademyManager = lazy(() => import('./pages/pro/AcademyManager'));

// Lazy Load Referee Zone
const RefereeOnboarding = lazy(() => import('./pages/referee/Onboarding'));
const RefereeDashboard = lazy(() => import('./pages/referee/Dashboard'));
const LiveScoring = lazy(() => import('./pages/referee/LiveScoring'));

// Lazy Load Player Zone
const PlayerLayout = lazy(() => import('./components/PlayerLayout'));
const PlayerCreateMatch = lazy(() => import('./pages/player/PlayerCreateMatch'));
const PlayerDashboard = lazy(() => import('./pages/player/PlayerDashboard'));
const PlayerTournamentDetails = lazy(() => import('./pages/Tournaments/TournamentDetails'));
const PlayerTournaments = lazy(() => import('./pages/player/PlayerTournaments'));
const PlayerSubscriptions = lazy(() => import('./pages/player/PlayerSubscriptions'));
const TournamentHistory = lazy(() => import('./pages/Tournaments/TournamentHistory').then(m => ({ default: m.TournamentHistory })));

// Lazy Load Heavy/Separated Pages
const LandingPage = lazy(() => import('./pages/web/LandingPage'));
const ClubManagerSolution = lazy(() => import('./pages/public/Solutions/ClubManager'));
const FederationSolution = lazy(() => import('./pages/public/Solutions/Federation'));
const RefereeSolution = lazy(() => import('./pages/public/Solutions/Referee'));
const PlayerSolution = lazy(() => import('./pages/public/Solutions/Player'));
const AppHome = lazy(() => import('./pages/mobile/AppHome'));
const MobileLanding = lazy(() => import('./pages/mobile/MobileLanding'));
const InstallPage = lazy(() => import('./pages/mobile/Install'));
const Notifications = lazy(() => import('./pages/mobile/Notifications'));
const PublicLayout = lazy(() => import('./components/PublicLayout'));
const MarketplaceHome = lazy(() => import('./pages/marketplace/MarketplaceHome'));
const CheckoutSuccess = lazy(() => import('./pages/marketplace/CheckoutSuccess'));
const HealthProfile = lazy(() => import('./pages/HealthProfile'));
const StravaCallback = lazy(() => import('./pages/StravaCallback'));
const CircleList = lazy(() => import('./pages/Circles/CircleList'));
const Feed = lazy(() => import('./pages/Feed/Feed'));
const PostDetails = lazy(() => import('./pages/Feed/PostDetails'));
const ClubFeedManager = lazy(() => import('./pages/admin/ClubFeedManager'));
const CalendarConnect = lazy(() => import('./pages/Integrations/CalendarConnect'));
const CircleDetails = lazy(() => import('./pages/Circles/CircleDetails'));
const ShopProfile = lazy(() => import('./pages/marketplace/ShopProfile'));
const ClubProfile = lazy(() => import('./pages/Club/ClubProfile'));
const DiscoveryPage = lazy(() => import('./pages/mobile/DiscoveryPage'));
const AcademyLessonDetail = lazy(() => import('./pages/AcademyLessonDetail'));
const MatchesPage = lazy(() => import('./pages/mobile/MatchesPage'));
const PublicProfile = lazy(() => import('./pages/mobile/PublicProfile'));

// Shared Heavy Pages
const SearchPage = lazy(() => import('./pages/Search'));
const Profile = lazy(() => import('./pages/Profile'));
const CreateMatch = lazy(() => import('./pages/CreateMatch'));
const MatchDetails = lazy(() => import('./pages/MatchDetails'));
const FullChatPage = lazy(() => import('./pages/FullChatPage'));
const CGU = lazy(() => import('./pages/CGU'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Contact = lazy(() => import('./pages/Contact'));

// Lazy Load Federation Zone
const FederationOnboarding = lazy(() => import('./pages/federation/FederationOnboarding'));
const FederationDashboard = lazy(() => import('./pages/federation/FederationDashboard'));
const FederationSettings = lazy(() => import('./pages/federation/FederationSettings'));
const FederationPlayers = lazy(() => import('./pages/federation/FederationPlayers'));
const FederationDocuments = lazy(() => import('./pages/federation/FederationDocuments'));
const FederationPlaceholder = lazy(() => import('./pages/federation/FederationPlaceholder'));
const FederationFinances = lazy(() => import('./pages/pro/federation/FederationFinances'));
const FederationFeed = lazy(() => import('./pages/federation/FederationFeed'));
const FederationClubs = lazy(() => import('./pages/federation/FederationClubs'));
const FederationReferees = lazy(() => import('./pages/federation/FederationReferees'));
const FederationProfile = lazy(() => import('./pages/federation/FederationProfile'));
const ClubAffiliation = lazy(() => import('./pages/pro/ClubAffiliation'));

// Loading Fallback
const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
);

import { api } from './services/api';

import { Toaster } from 'react-hot-toast';
import GlobalNotificationSystem from './components/GlobalNotificationSystem';
import { PWAInstallBanner } from './components/layout/PWAInstallBanner';

import ReloadPrompt from './components/ReloadPrompt';

function App() {
    const { t } = useTranslation(['common', 'install']);
    // Track PWA Installs natively
    useEffect(() => {
        const handler = () => {
            console.log("PWA Installed! Tracking...");
            api.system.trackInstall().catch(console.error);
        };
        window.addEventListener('appinstalled', handler);

        // Height script for mobile (solve 100vh gap issue)
        const updateHeight = () => {
            // Use visualViewport if available (more accurate on modern iOS)
            // Use requestAnimationFrame to avoid synchronous layout thrashing (PageSpeed Insights: "Ajustement forcé de la mise en page")
            const vh = (window.visualViewport ? window.visualViewport.height : window.innerHeight) * 0.01;
            requestAnimationFrame(() => {
                document.documentElement.style.setProperty('--vh', `${vh}px`);
            });
        };

        // Aggressive update for mobile Safari/PWA shifts
        window.addEventListener('resize', updateHeight);
        window.addEventListener('orientationchange', updateHeight);
        window.addEventListener('pageshow', updateHeight);
        window.addEventListener('focus', updateHeight);
        window.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') updateHeight();
        });

        updateHeight();
        // Safety delays for browser UI stabilization after external return
        setTimeout(updateHeight, 100);
        setTimeout(updateHeight, 500);

        return () => {
            window.removeEventListener('appinstalled', handler);
            window.removeEventListener('resize', updateHeight);
            window.removeEventListener('orientationchange', updateHeight);
            window.removeEventListener('pageshow', updateHeight);
            window.removeEventListener('focus', updateHeight);
        };
    }, []);

    return (
        <BrowserRouter>
            <ReloadPrompt />
            <QuotaProvider>
                <AuthProvider>
                    <CartProvider>
                        < Toaster position="top-center" toastOptions={{ duration: 4000, style: { background: '#1e293b', color: '#fff', borderRadius: '12px' } }} />
                        <GlobalNotificationSystem />
                        <PWAInstallBanner />
                        <MaintenanceGuard>
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    <Route path="/login" element={<Navigate to="/" replace />} />
                                    <Route path="/register" element={<Register />} />
                                    <Route path="/onboarding" element={<Onboarding />} />
                                    <Route path="/forgot-password" element={<ForgotPassword />} />
                                    <Route path="/reset-password" element={<ResetPassword />} />
                                    <Route path="/auth/callback" element={<AuthCallback />} />
                                    <Route path="/pro/welcome" element={<ManagerOnboarding />} />
                                    <Route path="/cgu" element={<CGU />} />
                                    <Route path="/privacy" element={<Privacy />} />
                                    <Route path="/contact" element={<Contact />} />
                                    <Route path="/federation/onboard" element={<FederationOnboarding />} />

                                    {/* Marketing Website (Public) */}
                                    <Route path="/" element={<LandingPage />} />
                                    {/* Legacy/Redirects handling */}
                                    <Route path="/appli" element={<Navigate to="/" replace />} />

                                    {/* Mobile Application (Restricted Zone) */}
                                    <Route path="/app" element={<Layout />}>
                                        <Route index element={<AppHome />} /> {/* Default to Home */}
                                        <Route path="home" element={<AppHome />} />
                                        <Route path="map" element={<Home />} />
                                        <Route path="search" element={<SearchPage />} />
                                        <Route path="profile" element={<Profile />} />
                                        <Route path="subscriptions" element={<PlayerSubscriptions />} />
                                        <Route path="health" element={<HealthProfile />} />
                                        <Route path="health/strava-callback" element={<StravaCallback />} />
                                        <Route path="create" element={<CreateMatch />} />
                                        <Route path="match/:id" element={<MatchDetails />} />
                                        <Route path="notifications" element={<Notifications />} />
                                        <Route path="integrations/calendar" element={<CalendarConnect />} />
                                        <Route path="feed" element={<Feed />} />
                                        <Route path="post/:id" element={<PostDetails />} />
                                        <Route path="circles" element={<CircleList />} />
                                        <Route path="circles/:id" element={<CircleDetails />} />
                                        <Route path="tournaments/history" element={<TournamentHistory />} />
                                        <Route path="tournaments/:id" element={<PlayerTournamentDetails />} />
                                        <Route path="discover" element={<MobileLanding />} />
                                        <Route path="match" element={<DiscoveryPage />} />
                                        <Route path="matches" element={<MatchesPage />} />
                                        <Route path="users/:id" element={<PublicProfile />} />
                                        <Route path="messages" element={<FullChatPage />} />
                                        <Route path="messages/:id" element={<FullChatPage />} />
                                        <Route path="pro" element={<AppProDashboard />} />
                                        <Route path="pro/planning" element={<AppProPlanning />} />
                                        <Route path="referee" element={<RefereeDashboard />} />
                                        <Route path="referee/onboarding" element={<RefereeOnboarding />} />
                                        <Route path="academy/lesson/:id" element={<AcademyLessonDetail />} />
                                        <Route path="marketplace" element={<MarketplaceHome />} />
                                        <Route path="marketplace/shop/:id" element={<ShopProfile />} />
                                        <Route path="marketplace/success" element={<CheckoutSuccess />} />
                                        <Route path="clubs/:id" element={<ClubProfile />} />
                                        <Route path="federations/:id" element={<FederationProfile />} />
                                        <Route path="pro/player/:id" element={<ProPlayerProfile />} />
                                        <Route path="pro/calendar" element={<ProTournamentCalendar />} />
                                        <Route path="pro/rankings" element={<ProRankings />} />
                                    </Route>

                                    {/* Public Install / Store Page */}
                                    <Route path="/install" element={<InstallPage />} />
                                    <Route path="/tv" element={<TVSetup />} />
                                    <Route path="/tournaments/:id/tv" element={<TournamentTV />} /> {/* Public TV View */}
                                    <Route path="/courts/:id/tv" element={<CourtTV />} /> {/* Court specific TV View */}
                                    <Route path="/tv/club/:id" element={<ClubTV />} /> {/* Club TV View */}
                                    <Route path="/club/:id/checkin" element={<CheckInPage />} /> {/* QR Code Target */}
                                    <Route path="/clubs/:id" element={<ClubProfile />} />
                                    <Route path="/federations/:id" element={<FederationProfile />} />
                                    {/* Standalone Live Scoring */}
                                    <Route path="/referee/live/:id" element={<LiveScoring />} />
                                    {/* Public Marketplace Zone */}
                                    <Route element={<PublicLayout />}>
                                        <Route path="/marketplace" element={<MarketplaceHome />} />
                                        <Route path="/marketplace/shop/:id" element={<ShopProfile />} />
                                        <Route path="/marketplace/success" element={<CheckoutSuccess />} />
                                        <Route path="/solutions/club-manager" element={<ClubManagerSolution />} />
                                        <Route path="/solutions/federation" element={<FederationSolution />} />
                                        <Route path="/solutions/referee" element={<RefereeSolution />} />
                                        <Route path="/solutions/player" element={<PlayerSolution />} />
                                    </Route>

                                    {/* Admin Zone (Desktop/Web) */}
                                    <Route path="/admin" element={<AdminLayout />}>
                                        <Route index element={<AdminDashboard />} />
                                        {/* Dedicated Chat Page (Internal) */}
                                        <Route path="chat" element={<FullChatPage />} />
                                        <Route path="chat/:id" element={<FullChatPage />} />

                                        <Route path="clubs" element={<AdminClubs />} />
                                        <Route path="users" element={<AdminUserList />} />
                                        <Route path="matches" element={<AdminMatchList />} />
                                        <Route path="matches/:id" element={<AdminMatchDetails />} />
                                        <Route path="validations" element={<AdminValidations />} />
                                        <Route path="messages" element={<AdminMessages />} />
                                        <Route path="settings" element={<AdminSettings />} />
                                        <Route path="showroom" element={<AdminShowroom />} />
                                        <Route path="plans" element={<AdminPlans />} />
                                        <Route path="notifications" element={<AdminPushSubscriptions />} />
                                        <Route path="feedbacks" element={<AdminFeedbacks />} />
                                        <Route path="feed" element={<AdminFeed />} />
                                        <Route path="social" element={<AdminSocial />} />
                                        <Route path="moderation" element={<AdminModeration />} />
                                        <Route path="tickets" element={<AdminTickets />} />
                                        <Route path="profile" element={<Profile />} />
                                        <Route path="polls" element={<AdminPolls />} />
                                        <Route path="transactions" element={<AdminTransactions />} />
                                        <Route path="tournaments" element={<AdminTournaments />} />
                                        <Route path="tournaments/create" element={<TournamentWizard />} />
                                        <Route path="tournaments/:id" element={<TournamentDetails />} />
                                        <Route path="preview" element={<AdminMobilePreview />} />
                                        <Route path="federations" element={<AdminFederations />} />
                                        <Route path="shop" element={<AdminShop />} />
                                    </Route>

                                    {/* Pro Zone (Desktop/Web) - Manager */}
                                    <Route path="/pro" element={<ProLayout />}>
                                        <Route index element={<ProDashboard />} />
                                        <Route path="messages" element={<FullChatPage />} />
                                        <Route path="messages/:id" element={<FullChatPage />} />
                                        <Route path="planning" element={<ProPlanning />} />
                                        <Route path="courts" element={<ProCourts />} />
                                        <Route path="layout" element={<LayoutEditor />} />
                                        <Route path="supervision" element={<Supervision />} />
                                        <Route path="academy" element={<AcademyManager />} />
                                        <Route path="devices" element={<DeviceManager />} />
                                        <Route path="automations" element={<Automations />} />
                                        <Route path="settings" element={<ProSettings />} />
                                        <Route path="feed" element={<ClubFeedManager />} />
                                        <Route path="scan" element={<div className="p-10 text-center">{t('coming_soon')}</div>} />
                                        <Route path="support" element={<ProTickets />} />
                                        <Route path="sponsors" element={<ProSponsors />} />
                                        <Route path="marketplace" element={<ShopStatus />} />
                                        <Route path="marketplace/request" element={<ShopRequestForm />} />
                                        <Route path="marketplace/status" element={<ShopStatus />} />
                                        <Route path="marketplace/settings" element={<ShopSettings />} />
                                        <Route path="marketplace/catalog" element={<MarketplaceCatalog />} />
                                        <Route path="marketplace/orders" element={<MerchantOrders />} />
                                        <Route path="marketplace/reviews" element={<MarketplaceReviews />} />
                                        <Route path="reviews" element={<ClubReviews />} />
                                        <Route path="marketplace/inventory" element={<MarketplaceInventory />} />
                                        <Route path="marketplace/suppliers" element={<MarketplaceSuppliers />} />
                                        <Route path="transactions" element={<ProTransactions />} />
                                        <Route path="team" element={<ProTeam />} />
                                        <Route path="players" element={<ProPlayers />} />
                                        <Route path="rentals" element={<RentalMonitor />} />
                                        <Route path="subscriptions" element={<SubscriptionManager />} />
                                        <Route path="profile" element={<ProProfile />} />
                                        <Route path="tournaments" element={<AdminTournaments />} />
                                        <Route path="tournaments/create" element={<TournamentWizard />} />
                                        <Route path="tournaments/:id" element={<TournamentDetails />} />
                                        <Route path="federation" element={<FederationDashboard />} />
                                        <Route path="federation/clubs" element={<FederationClubs />} />
                                        <Route path="federation/players" element={<FederationPlayers />} />
                                        <Route path="federation/referees" element={<FederationReferees />} />
                                        <Route path="federation/feed" element={<FederationFeed />} />
                                        <Route path="federation/finances" element={<FederationFinances />} />
                                        <Route path="federation/settings" element={<FederationSettings />} />
                                        <Route path="affiliation" element={<ClubAffiliation />} />
                                        <Route path="federation/tournaments" element={<FederationPlaceholder />} />
                                        <Route path="federation/documents" element={<FederationDocuments />} />
                                        <Route path="referee" element={<RefereeDashboard />} />
                                        <Route path="referee/onboarding" element={<RefereeOnboarding />} />
                                    </Route>

                                    {/* Player Zone (Desktop/Web) */}
                                    <Route path="/player" element={<PlayerLayout />}>
                                        <Route index element={<PlayerDashboard />} />
                                        <Route path="orders" element={<Navigate to="/marketplace?tab=orders" replace />} />
                                        <Route path="messages" element={<FullChatPage />} />
                                        <Route path="messages/:id" element={<FullChatPage />} />
                                        <Route path="map" element={<Home />} />
                                        <Route path="search" element={<SearchPage />} />
                                        <Route path="feed" element={<Feed />} />
                                        <Route path="post/:id" element={<PostDetails />} />
                                        <Route path="circles" element={<CircleList />} />
                                        <Route path="circles/:id" element={<CircleDetails />} />
                                        <Route path="tournaments" element={<PlayerTournaments />} />
                                        <Route path="discover" element={<MobileLanding />} />
                                        <Route path="match" element={<DiscoveryPage />} />
                                        <Route path="matches" element={<MatchesPage />} />
                                        <Route path="create" element={<PlayerCreateMatch />} />
                                        <Route path="profile" element={<Profile />} />
                                        <Route path="integrations/calendar" element={<CalendarConnect />} />
                                        <Route path="match/:id" element={<MatchDetails />} />
                                        <Route path="tournaments/history" element={<TournamentHistory />} />
                                        <Route path="tournaments/:id" element={<PlayerTournamentDetails />} />
                                        <Route path="referee" element={<RefereeDashboard />} />
                                        <Route path="referee/onboarding" element={<RefereeOnboarding />} />
                                        <Route path="notifications" element={<Notifications />} />
                                        <Route path="health" element={<HealthProfile />} />
                                        <Route path="clubs/:id" element={<ClubProfile />} />
                                        <Route path="federations/:id" element={<FederationProfile />} />
                                        <Route path="pro/player/:id" element={<ProPlayerProfile />} />
                                        <Route path="pro/calendar" element={<ProTournamentCalendar />} />
                                        <Route path="pro/rankings" element={<ProRankings />} />
                                        <Route path="pro" element={<AppProDashboard />} />
                                        <Route path="pro/planning" element={<AppProPlanning />} />
                                    </Route>

                                    {/* Catch all - Smart Redirect */}
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </Suspense>
                        </MaintenanceGuard>
                    </CartProvider>
                </AuthProvider>
            </QuotaProvider>
        </BrowserRouter>
    );
}

export default App;
